'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

interface TutorialTooltipProps {
  /** The ref to the target element to point at */
  readonly targetRef: React.RefObject<HTMLElement | null>;
  /** Whether the tooltip should display */
  readonly isVisible: boolean;
  /** Called when tooltip is dismissed */
  readonly onDismiss: () => void;
  /** Auto-dismiss delay in ms (default: 6000) */
  readonly autoDismissMs?: number;
}

export function TutorialTooltip({
  targetRef,
  isVisible,
  onDismiss,
  autoDismissMs = 6000,
}: TutorialTooltipProps) {
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties | null>(null);
  const [isShowing, setIsShowing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const computePosition = useCallback(() => {
    if (!targetRef.current) return;
    const rect = targetRef.current.getBoundingClientRect();

    // Position tooltip below the target element, centered
    const top = rect.bottom + 12;
    let left = rect.left + rect.width / 2;

    // Clamp so it doesn't overflow screen edges
    const tooltipWidth = 290;
    const minLeft = tooltipWidth / 2 + 16;
    const maxLeft = window.innerWidth - tooltipWidth / 2 - 16;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    setTooltipStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      transform: 'translateX(-50%)',
    });
  }, [targetRef]);

  // Show with a small delay after becoming visible
  useEffect(() => {
    if (!isVisible) {
      setIsShowing(false);
      return;
    }

    const showTimer = setTimeout(() => {
      computePosition();
      setIsShowing(true);
    }, 600);

    return () => clearTimeout(showTimer);
  }, [isVisible, computePosition]);

  // Recompute on resize
  useEffect(() => {
    if (!isShowing) return;

    const handleResize = () => computePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [isShowing, computePosition]);

  // Auto-dismiss after delay
  useEffect(() => {
    if (!isShowing) return;

    timerRef.current = setTimeout(() => {
      setIsShowing(false);
      setTimeout(onDismiss, 300); // Wait for fade-out
    }, autoDismissMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isShowing, autoDismissMs, onDismiss]);

  if (!isVisible || !tooltipStyle) return null;

  return (
    <div
      className={`z-[10001] pointer-events-none transition-all duration-300 ${
        isShowing ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
      style={tooltipStyle}
    >
      {/* Arrow pointing up */}
      <div className="flex justify-center mb-[-1px]">
        <div
          className="h-3 w-3 rotate-45 bg-[#3b005a] border-l border-t border-white/10"
        />
      </div>

      {/* Tooltip body */}
      <div className="max-w-[290px] rounded-[14px] bg-[#3b005a] border border-white/10 px-4 py-3">
        <p className="text-sm text-white leading-relaxed text-center">
          Clique aqui sempre que precisar revisar o tutorial ou aprender sobre novas funcionalidades! 🎓
        </p>
      </div>
    </div>
  );
}
